import { localStore } from './storage';
import { CustomFoodItem } from './types';

export interface GeminiTestResult {
  ok: boolean;
  model?: string;
  error?: string;
  availableModels?: string[];
}

export interface VisionAnalysisResult {
  success: boolean;
  suggested_name?: string;
  dish_name?: string;
  suggested_category?: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink';
  estimated_total_grams?: number;
  ingredients_tags?: string[];
  ingredients?: Array<{
    name: string;
    amount?: number;
    unit?: string;
    grams?: number;
    calories?: number;
    protein?: number;
    fat?: number;
    carbs?: number;
  }>;
  discovered_foods?: CustomFoodItem[];
  notes?: string;
  error?: string;
}

export interface MealDecompositionResult {
  dish_name: string;
  meal_category: 'Breakfast' | 'Lunch' | 'Dinner' | 'Snack' | 'Drink';
  portion_size?: number;
  ingredients: Array<{
    name: string;
    amount: number;
    unit: string;
    grams: number;
    calories?: number;
    protein?: number;
    fat?: number;
    carbs?: number;
  }>;
  discovered_foods?: CustomFoodItem[];
  notes?: string;
}

function extractJson(text: string): any {
  if (!text) return null;
  let clean = text.trim();
  const fence = clean.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fence) clean = fence[1].trim();
  try {
    return JSON.parse(clean);
  } catch {}

  const firstBrace = clean.indexOf('{');
  const lastBrace = clean.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    try {
      return JSON.parse(clean.slice(firstBrace, lastBrace + 1));
    } catch {}
  }

  const firstBracket = clean.indexOf('[');
  const lastBracket = clean.lastIndexOf(']');
  if (firstBracket !== -1 && lastBracket > firstBracket) {
    try {
      const arr = JSON.parse(clean.slice(firstBracket, lastBracket + 1));
      return { ingredients_tags: arr };
    } catch {}
  }

  return null;
}

/**
 * Constructs the canonical generateContent URL for Google Gemini API.
 * Handles any input format (with or without 'v1beta/', 'models/', etc.)
 * and ensures the correct https://generativelanguage.googleapis.com/{ver}/models/{model}:generateContent structure.
 */
export function buildGeminiGenerateUrl(modelIdentifier: string, apiKey: string, defaultVersion = 'v1beta'): string {
  let clean = (modelIdentifier || '').trim();
  let version = defaultVersion;

  if (clean.startsWith('v1/')) {
    version = 'v1';
    clean = clean.replace(/^v1\//, '');
  } else if (clean.startsWith('v1beta/')) {
    version = 'v1beta';
    clean = clean.replace(/^v1beta\//, '');
  }

  clean = clean.replace(/^models\//, '');

  return `https://generativelanguage.googleapis.com/${version}/models/${clean}:generateContent?key=${apiKey.trim()}`;
}

/**
 * Discovers and tests available Gemini models for the provided API key.
 */
export async function testAndSelectGeminiModel(apiKey: string): Promise<GeminiTestResult> {
  const cleanKey = apiKey.trim();
  if (!cleanKey) {
    return { ok: false, error: 'Please enter a valid Gemini API key.' };
  }

  let discoveredModels: string[] = [];

  // 1. Query ListModels from v1beta and v1
  for (const ver of ['v1beta', 'v1']) {
    try {
      const listRes = await fetch(
        `https://generativelanguage.googleapis.com/${ver}/models?key=${cleanKey}`
      );
      if (listRes.ok) {
        const data = await listRes.json();
        if (Array.isArray(data.models)) {
          const names = data.models
            .filter((m: any) =>
              Array.isArray(m.supportedGenerationMethods)
                ? m.supportedGenerationMethods.includes('generateContent')
                : true
            )
            .map((m: any) => m.name.replace(/^models\//, ''))
            .filter((name: string) => !name.toLowerCase().includes('tts'));
          for (const n of names) {
            if (!discoveredModels.includes(n)) {
              discoveredModels.push(n);
            }
          }
        }
      }
    } catch (err) {
      console.warn(`Failed to list models for ${ver}:`, err);
    }
  }

  // 2. Ordered preference of high-speed flash and multimodal models
  const preferredModels = [
    'gemini-3.5-flash-lite',
    'gemini-3.5-flash',
    'gemini-flash-lite-latest',
    'gemini-2.0-flash-lite',
    'gemini-2.0-flash',
    'gemini-1.5-flash',
    'gemini-2.5-flash',
    'gemini-1.5-flash-8b',
    'gemini-1.5-pro',
  ];

  const candidateModels: string[] = [];
  if (discoveredModels.length > 0) {
    for (const p of preferredModels) {
      if (discoveredModels.includes(p)) candidateModels.push(p);
    }
    for (const d of discoveredModels) {
      if (!candidateModels.includes(d)) candidateModels.push(d);
    }
  } else {
    candidateModels.push(...preferredModels);
  }

  // 3. Test generateContent on candidates
  let lastErrorMessage = '';
  for (const model of candidateModels) {
    for (const ver of ['v1beta', 'v1']) {
      try {
        const testUrl = buildGeminiGenerateUrl(model, cleanKey, ver);
        const testRes = await fetch(
          testUrl,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: 'Respond with OK.' }] }],
            }),
          }
        );

        if (testRes.ok) {
          const testData = await testRes.json();
          if (testData.candidates?.[0]?.content?.parts?.[0]?.text) {
            const verifiedPath = `${ver}/${model}`;
            localStore.saveGeminiApiKey(cleanKey);
            localStore.saveGeminiModel(verifiedPath);
            return {
              ok: true,
              model: verifiedPath,
              availableModels: discoveredModels,
            };
          }
        } else {
          const errJson = await testRes.json().catch(() => ({}));
          lastErrorMessage = errJson?.error?.message || `HTTP ${testRes.status}`;
        }
      } catch (err: any) {
        lastErrorMessage = err.message || 'Network request failed';
      }
    }
  }

  return {
    ok: false,
    error: lastErrorMessage || 'No compatible Gemini model found for this key.',
    availableModels: discoveredModels,
  };
}

/**
 * Direct Client-Side Gemini Vision Call with graceful backend proxy fallback.
 */
export async function analyzeImageDirectOrProxy(
  rawBase64: string,
  currentDescription: string = ''
): Promise<VisionAnalysisResult> {
  const geminiKey = localStore.getGeminiApiKey();
  const configuredModel = localStore.getGeminiModel() || 'v1beta/gemini-3.5-flash-lite';
  const currentUserId = localStore.getCurrentUserId();

  const mimeMatch = rawBase64.match(/^data:([^;]+);base64,/);
  const detectedMime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const cleanBase64 = rawBase64.replace(/^data:[^;]+;base64,/, '').trim();

  const prompt = `You are an expert food vision analyst and clinical dietitian specializing in Monash Low FODMAP evaluation.
ANALYZE THE FOOD IMAGE (Plate, Dish, or Packaged Product) AND PROVIDE:
1. "suggested_name": The specific name of the dish or packaged product in Title Case (e.g. "Chicken and Oyster Mushroom Risotto", "Gluten-Free Seed Bread with Avocado").
2. "suggested_category": One of "Breakfast", "Lunch", "Dinner", "Snack", "Drink".
3. "estimated_total_grams": Estimated total portion weight in grams (e.g. 350).
4. "ingredients_tags": Array of distinct ingredient names as clean strings (e.g. ["Chicken Breast", "Oyster Mushrooms", "Arborio Rice", "Parmesan Cheese", "Olive Oil"]).
5. "ingredients": Array of individual ingredients with realistic portion weights and macro estimates:
   [{"name": "Chicken Breast", "amount": 120, "unit": "g", "grams": 120, "calories": 198, "protein": 37, "fat": 4, "carbs": 0}]
6. "discovered_foods": Array of ingredients with Monash FODMAP ratings:
   [{"name": "Oyster Mushrooms", "category": "vegetables", "rating": "low", "primary_culprit": "None", "safe_grams": 75, "safe_serving_title": "1 cup (75g)", "notes": "Safe at 75g according to Monash", "swap_suggestion": "None needed"}]
7. "notes": 1-2 concise sentences summarizing the FODMAP safety profile and any potential culprits (fructans, lactose, excess fructose, polyols, GOS).

CRITICAL VISION RULES:
- Inspect visible plating geometry, food item textures, garnishes, cooking preparations (e.g., grilled, roasted, fried), and any visible packaged ingredient text/labels.
- Infer hidden culinary cooking mediums (e.g., olive oil, butter, garlic oil).
- Ensure individual ingredient gram weights add up reasonably to the estimated total weight.
- ZERO emojis anywhere.

Respond ONLY with valid JSON matching this exact structure:
{
  "suggested_name": "Dish Name in Title Case",
  "suggested_category": "Lunch",
  "estimated_total_grams": 350,
  "ingredients_tags": ["Ingredient 1", "Ingredient 2", "Ingredient 3"],
  "ingredients": [
    { "name": "Ingredient 1", "amount": 100, "unit": "g", "grams": 100, "calories": 150, "protein": 10, "fat": 5, "carbs": 15 }
  ],
  "discovered_foods": [],
  "notes": "Dietary summary"
}`;

  const normalizeVisionResult = (parsed: any): VisionAnalysisResult | null => {
    if (!parsed || typeof parsed !== 'object') return null;

    const suggestedName =
      parsed.suggested_name ||
      parsed.dish_name ||
      parsed.title ||
      parsed.name ||
      parsed.food_name ||
      '';

    let rawTags: string[] = [];
    if (Array.isArray(parsed.ingredients_tags)) {
      rawTags = parsed.ingredients_tags.map((t: any) => (typeof t === 'string' ? t : t?.name || '')).filter(Boolean);
    } else if (Array.isArray(parsed.ingredients)) {
      rawTags = parsed.ingredients.map((t: any) => (typeof t === 'string' ? t : t?.name || '')).filter(Boolean);
    }

    let parsedIngredients: any[] = [];
    if (Array.isArray(parsed.ingredients)) {
      parsedIngredients = parsed.ingredients
        .filter((item: any) => item && (typeof item === 'object' || typeof item === 'string'))
        .map((item: any) => {
          if (typeof item === 'string') {
            return { name: item, amount: 100, unit: 'g', grams: 100 };
          }
          return {
            name: item.name || 'Ingredient',
            amount: Number(item.amount || item.grams || 100),
            unit: item.unit || 'g',
            grams: Number(item.grams || item.amount || 100),
            calories: Number(item.calories || 0),
            protein: Number(item.protein || 0),
            fat: Number(item.fat || 0),
            carbs: Number(item.carbs || 0),
          };
        });
    }

    if (suggestedName || rawTags.length > 0 || parsedIngredients.length > 0) {
      let cat = parsed.suggested_category || parsed.category || parsed.meal_category;
      if (!['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Drink'].includes(cat)) {
        cat = 'Lunch';
      }

      return {
        success: true,
        suggested_name: suggestedName || 'Analyzed Plate',
        dish_name: suggestedName || 'Analyzed Plate',
        suggested_category: cat,
        estimated_total_grams: Number(parsed.estimated_total_grams) || 350,
        ingredients_tags: rawTags.length > 0 ? rawTags : parsedIngredients.map((i) => i.name),
        ingredients: parsedIngredients,
        discovered_foods: Array.isArray(parsed.discovered_foods) ? parsed.discovered_foods : [],
        notes: parsed.notes || '',
      };
    }

    return null;
  };

  // 1. Direct Client-to-Gemini Call (Fastest, zero proxy overhead)
  if (geminiKey) {
    const candidateModels = [
      configuredModel,
      'gemini-3.5-flash-lite',
      'gemini-3.5-flash',
      'gemini-flash-lite-latest',
      'gemini-2.0-flash-lite',
    ];
    const uniqueModels = Array.from(new Set(candidateModels));

    for (const mPath of uniqueModels) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 9000);
        const url = buildGeminiGenerateUrl(mPath, geminiKey);
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  { text: `${prompt}\n${currentDescription ? `Context: "${currentDescription}"` : ''}` },
                  {
                    inlineData: {
                      mimeType: detectedMime,
                      data: cleanBase64,
                    },
                  },
                ],
              },
            ],
            generationConfig: {
              temperature: 0.1,
              response_mime_type: 'application/json',
              maxOutputTokens: 3000,
            },
          }),
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text;
          if (rawText) {
            const parsed = extractJson(rawText);
            const normalized = normalizeVisionResult(parsed);
            if (normalized) {
              return normalized;
            }
          }
        } else {
          const errData = await res.json().catch(() => ({}));
          console.warn(`Direct Gemini vision call to ${mPath} returned HTTP ${res.status}:`, errData);
        }
      } catch (err) {
        console.warn(`Direct Gemini vision call to ${mPath} failed, trying next:`, err);
      }
    }
  }

  // 2. Cloudflare Proxy Fallback
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);
    const res = await fetch('/api/vision', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': currentUserId,
      },
      signal: controller.signal,
      body: JSON.stringify({
        imageBase64: rawBase64,
        currentDescription,
        geminiApiKey: geminiKey || undefined,
        geminiModel: configuredModel,
        userId: currentUserId,
      }),
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data && data.success) {
        return data;
      }
    }
  } catch (err) {
    console.warn('Backend proxy vision call failed:', err);
  }

  return {
    success: false,
    error: 'Could not analyze photo. Please try again or type ingredients manually.',
  };
}

/**
 * Direct Client-Side Gemini Meal Decomposition Call with proxy fallback.
 */
export async function describeMealDirectOrProxy(
  text: string
): Promise<{ success: boolean; data?: MealDecompositionResult; error?: string }> {
  const geminiKey = localStore.getGeminiApiKey();
  const configuredModel = localStore.getGeminiModel() || 'v1beta/gemini-3.5-flash-lite';
  const currentUserId = localStore.getCurrentUserId();

  const systemPrompt = `You are an expert clinical dietitian and Monash Low FODMAP meal decomposition engine.
ANALYZE THE NATURAL LANGUAGE MEAL DESCRIPTION AND PROVIDE:
1. "dish_name": Specific dish or meal name in Title Case.
2. "meal_category": One of "Breakfast", "Lunch", "Dinner", "Snack", "Drink".
3. "portion_size": 1.0 (or fraction eaten if specified in text like "had half a bowl").
4. "ingredients": Array of distinct culinary ingredients (4 to 8 core ingredients) with realistic individual gram weights and macronutrients.
5. "discovered_foods": Array of ingredients with Monash FODMAP classification (rating: "low" | "moderate" | "high", primary_culprit: "None" | "Fructans" | "Lactose" | "Sorbitol" | "Mannitol" | "Fructose" | "GOS", safe_grams, swap_suggestion).
6. "notes": 1-2 concise sentences on FODMAP safety and culprit breakdown.

CRITICAL NATURAL LANGUAGE RULES:
- Decompose colloquial phrases and composite dishes into individual base foods (e.g., "bacon and eggs on toast" -> ["Back Bacon", "Egg", "Sourdough Bread", "Butter"]).
- Accurately convert colloquial measurements ("2 slices", "1 tbsp", "half an avocado", "1 bowl") into realistic culinary gram weights (e.g., 2 slices bread = 70g, 1 avocado = 150g, 1 tbsp butter = 15g).
- If recipe yield or servings are mentioned (e.g. "cooked a 4-serving pasta, ate 1 portion"), calculate the single eaten portion.
- ZERO emojis anywhere.

Output strictly valid JSON:
{
  "dish_name": "Dish Name in Title Case",
  "meal_category": "Lunch",
  "portion_size": 1.0,
  "ingredients": [
    { "name": "Ingredient Name", "amount": 100, "unit": "g", "grams": 100, "calories": 120, "protein": 4, "fat": 1, "carbs": 24 }
  ],
  "discovered_foods": [],
  "notes": "FODMAP analysis"
}`;

  // 1. Direct Gemini Call
  if (geminiKey) {
    const candidateModels = [
      configuredModel,
      'gemini-3.5-flash-lite',
      'gemini-3.5-flash',
      'gemini-flash-lite-latest',
      'gemini-2.0-flash-lite',
      'gemini-2.0-flash',
      'gemini-1.5-flash',
    ];
    const uniqueModels = Array.from(new Set(candidateModels));

    for (const mPath of uniqueModels) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const url = buildGeminiGenerateUrl(mPath, geminiKey);
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            contents: [
              {
                parts: [{ text: `${systemPrompt}\n\nMeal description: "${text}"` }],
              },
            ],
            generationConfig: {
              temperature: 0.1,
              response_mime_type: 'application/json',
              maxOutputTokens: 2000,
            },
          }),
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          const rawText = data?.candidates?.[0]?.content?.parts?.[0]?.text;
          if (rawText) {
            const parsed = extractJson(rawText);
            if (parsed && (parsed.dish_name || parsed.ingredients)) {
              return { success: true, data: parsed };
            }
          }
        }
      } catch (err) {
        console.warn(`Direct Gemini describe call to ${mPath} failed:`, err);
      }
    }
  }

  // 2. Cloudflare Proxy Fallback
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);
    const res = await fetch('/api/describe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': currentUserId,
      },
      signal: controller.signal,
      body: JSON.stringify({
        text,
        geminiApiKey: geminiKey || undefined,
        geminiModel: configuredModel,
        userId: currentUserId,
      }),
    });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (data && (data.dish_name || data.ingredients)) {
        return { success: true, data };
      }
    }
  } catch (err) {
    console.warn('Backend proxy describe call failed:', err);
  }

  return { success: false, error: 'Could not decompose meal description.' };
}

